import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import useAuth from "../../hooks/useAuth";
import { useSelector, useDispatch } from "react-redux";
import { useFilterNavigation } from "../../hooks/useFilterNavigation";
import { setUsersFilters, clearUsersFilters } from "../../redux/slices/usersFilters";
import { DataGrid, GridRowEditStopReasons } from "@mui/x-data-grid";

import {
  Avatar,
  Button,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Link,
  Card as MuiCard,
  Divider as MuiDivider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { deepOrange } from "@mui/material/colors";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

function DataGridUsers() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getData } = useAuth();
  const savedSearchParam = useSelector((state) => state.usersFilters);

  const [usersList, setUsersList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParam, setSearchParam] = useState(savedSearchParam || "");
  const [filteredUsersList, setFilteredUsersList] = useState(usersList);
  const [isNewUserDialogOpen, setIsNewUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    nome: "",
    cognome: "",
    codiceFiscale: "",
  });
  const [newUserErrors, setNewUserErrors] = useState({});

  useFilterNavigation(
    () => {
      if (savedSearchParam) {
        setSearchParam(savedSearchParam);
      }
    },
    () => {
      dispatch(clearUsersFilters());
      setSearchParam("");
    }
  );

  const updateState = (jsonData) => {
    setUsersList(jsonData);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchParam(value);
    dispatch(setUsersFilters(value));
  };

  const handleOpenNewUserDialog = () => {
    setIsNewUserDialogOpen(true);
  };

  const handleCloseNewUserDialog = () => {
    setIsNewUserDialogOpen(false);
    setNewUser({
      email: "",
      nome: "",
      cognome: "",
      codiceFiscale: "",
    });
    setNewUserErrors({});
  };

  const handleNewUserChange = (event) => {
    const { name, value } = event.target;
    const normalizedValue =
      name === "codiceFiscale" ? value.toUpperCase() : value;

    setNewUser((currentUser) => ({
      ...currentUser,
      [name]: normalizedValue,
    }));
    setNewUserErrors((currentErrors) => ({
      ...currentErrors,
      [name]: "",
    }));
  };

  const validateNewUser = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/;
    const codiceFiscaleRegex =
      /^([A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z])$/;

    if (!emailRegex.test(newUser.email.trim())) {
      errors.email = "Inserisci un indirizzo email valido.";
    }
    if (!nameRegex.test(newUser.nome.trim())) {
      errors.nome = "Inserisci un nome valido.";
    }
    if (!nameRegex.test(newUser.cognome.trim())) {
      errors.cognome = "Inserisci un cognome valido.";
    }
    if (!codiceFiscaleRegex.test(newUser.codiceFiscale.trim())) {
      errors.codiceFiscale = "Inserisci un codice fiscale valido di 16 caratteri.";
    }

    setNewUserErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveNewUser = (event) => {
    event.preventDefault();

    if (!validateNewUser()) {
      return;
    }

    const createdUser = {
      IdUID: `new-user-${Date.now()}`,
      Nominativo: `${newUser.nome.trim()} ${newUser.cognome.trim()}`,
      Avatar: `${newUser.nome.trim().charAt(0)}${newUser.cognome
        .trim()
        .charAt(0)}`.toUpperCase(),
      Email: newUser.email.trim(),
      CF: newUser.codiceFiscale.trim(),
      Ruolo: "",
      Stato: "Non Attivo",
      Azienda: "",
      Responsabile: "",
      CreatoDa: "",
      DataCreazione: new Intl.DateTimeFormat("it-IT").format(new Date()),
    };

    setUsersList((currentUsers) => [...currentUsers, createdUser]);
    handleCloseNewUserDialog();
  };

  useEffect(() => {
    const filteredList = usersList.filter((user) =>
      user.Nominativo.toLowerCase().includes(searchParam.toLowerCase())
    );
    setFilteredUsersList(filteredList);
  }, [searchParam, usersList]);

  useEffect(() => {
    getData({ action: "usersList" })
      .then((data) => {
        if (data.status === 0) {
          navigate("/private");
        }

        setIsLoading(true);
        return data.data;
      })
      .catch((e) => {
        return getData({ action: "usersList" });
      })
      .then((data) => {
        if (!data.alreadySet) {
          updateState(data);
        }
      })
      .catch((e) => {
        console.log("Reload failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pageSize, setPageSize] = useState(5);

  const [rowModesModel, setRowModesModel] = useState({});

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow) => {
    const updatedRow = { ...newRow, isNew: false };
    return updatedRow;
  };

  const renderChip = (value) => {
    let chipColor;

    if (value === "Attivo") {
      chipColor = "success";
    } else if (value === "Non Attivo") {
      chipColor = "error";
    } else {
      chipColor = "default";
    }

    return (
      <Chip
        style={{ color: "white" }}
        label={value}
        color={chipColor}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        size="small"
      />
    );
  };

  const columnsModelDataUsers = [
    {
      field: "Nominativo",
      headerName: "Nominativo",
      minWidth: 200,
      width: 300,
      flex: 1.5,
      renderCell: (params) => (
        <>
          <Avatar
            sx={{
              mr: 3,
              bgcolor: deepOrange[500],
              fontWeight: "bold",
              fontSize: 15,
            }}
          >
            {params.row.Avatar}
          </Avatar>
          <Link
            onClick={() => {
              const currentState = window.history.state;
              window.history.replaceState(
                { ...currentState, usr: { ...currentState?.usr, fromDetails: true } },
                "",
              );
              navigate(`/pages/profile/${params.id}`, {
                state: {
                  guiIdSubUser: params.id,
                  typeuser: "super",
                  fromPath: window.location.pathname,
                },
              });
            }}
            style={{ cursor: "pointer" }}
          >
            {params.value}
          </Link>
        </>
      ),
    },
    {
      field: "Ruolo",
      headerName: "Ruolo",
      minWidth: 100,
      width: 100,
      flex: 0.5,
    },
    {
      field: "Stato",
      headerName: "Stato",
      minWidth: 150,
      width: 200,
      flex: 1,
      align: "center",
      renderCell: (params) => renderChip(params.value),
    },
    {
      field: "Azienda",
      headerName: "Azienda",
      minWidth: 150,
      width: 200,
      flex: 1,
    },
    {
      field: "Responsabile",
      headerName: "Responsabile",
      minWidth: 150,
      width: 200,
      flex: 1,
    },
    {
      field: "CreatoDa",
      headerName: "Creato da",
      minWidth: 150,
      width: 200,
      flex: 1,
    },
    {
      field: "DataCreazione",
      headerName: "Data creazione",
      minWidth: 150,
      width: 200,
      flex: 1,
    },
  ];

  return isLoading ? (
    <>
      <Card mb={6}>
        <CardContent pb={1}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h6" gutterBottom>
                Risultati: {usersList.length}
              </Typography>
            </Grid>
            <Stack direction={"row"}>
              <FormControl sx={{ maxHeight: "30px", maxWidth: "200px" }}>
                <TextField
                  size="small"
                  type="text"
                  value={searchParam}
                  placeholder="Cerca per nominativo"
                  onChange={(e) => handleSearch(e)}
                  sx={{
                    marginRight: "2rem",
                    maxHeight: "30px",
                    "& .MuiInputBase-root": {
                      height: "30px",
                    },
                    "& .MuiInputBase-input": {
                      height: "100%",
                      padding: "0 14px",
                    },
                  }}
                />
              </FormControl>
              <Grid item>
                <Button
                  mr={2}
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleOpenNewUserDialog}
                >
                  Nuovo utente
                </Button>
              </Grid>
            </Stack>
          </Grid>
        </CardContent>
        <Paper>
          <div style={{ width: "100%" }}>
            <DataGrid
              autoHeight
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 20]}
              pagination
              getRowId={(usersList) => usersList.IdUID}
              rows={searchParam ? filteredUsersList : usersList}
              columns={columnsModelDataUsers}
              editMode="row"
              allowUpdating={true}
              rowModesModel={rowModesModel}
              onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
              experimentalFeatures={{ newEditingApi: true }}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
            />
          </div>
        </Paper>
      </Card>

      <Dialog
        open={isNewUserDialogOpen}
        onClose={handleCloseNewUserDialog}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={handleSaveNewUser} noValidate>
          <DialogTitle>Nuovo utente</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <TextField
                autoFocus
                required
                fullWidth
                type="email"
                label="Email utente"
                name="email"
                value={newUser.email}
                onChange={handleNewUserChange}
                error={Boolean(newUserErrors.email)}
                helperText={newUserErrors.email}
              />
              <TextField
                required
                fullWidth
                label="Nome"
                name="nome"
                value={newUser.nome}
                onChange={handleNewUserChange}
                error={Boolean(newUserErrors.nome)}
                helperText={newUserErrors.nome}
              />
              <TextField
                required
                fullWidth
                label="Cognome"
                name="cognome"
                value={newUser.cognome}
                onChange={handleNewUserChange}
                error={Boolean(newUserErrors.cognome)}
                helperText={newUserErrors.cognome}
              />
              <TextField
                required
                fullWidth
                label="Codice fiscale"
                name="codiceFiscale"
                value={newUser.codiceFiscale}
                onChange={handleNewUserChange}
                error={Boolean(newUserErrors.codiceFiscale)}
                helperText={newUserErrors.codiceFiscale}
                inputProps={{ maxLength: 16 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button type="button" onClick={handleCloseNewUserDialog}>
              Annulla
            </Button>
            <Button type="submit" variant="contained">
              Salva
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  ) : null;
}

function Users(props) {
  return (
    <React.Fragment>
      <Helmet title="Utenti" />
      <Typography variant="h3" gutterBottom display="inline">
        Utenti
      </Typography>
      <Divider my={6} />
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <DataGridUsers />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default Users;
